/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage, useIntl} from 'react-intl'
// Components
import {Box, Heading, Text, Flex} from '@salesforce/retail-react-app/app/components/shared/ui'

// Project Components
import Breadcrumb from '../../../components/breadcrumb'
import OptimizelyArea from '../../../components/optimizely/common/optimizelyArea'
import Seo from '../../../components/seo'

const PageHeader = ({
    category,
    productSearchResult,
    searchQuery,
    categoryContentData,
    isCategoryContentLoading,
    ...otherProps
}) => {
    const {formatMessage} = useIntl()

    const searchResultsPageTitle = formatMessage({
        id: 'search.results.page.title',
        defaultMessage: 'Search | Garage Clothing'
    })

    // Determine if we should show the category title
    // For search: always show title
    // For PLP: wait for Optimizely data to load, then decide based on ShowCategoryTitle flag
    const hasOptimizelyContent = !!categoryContentData?.items?.[0]
    const showCategoryTitleFlag = categoryContentData?.items?.[0]?.ShowCategoryTitle === true

    // Show title if:
    // 1. It's a search query, OR
    // 2. Optimizely data is loaded AND (there's no content OR ShowCategoryTitle is true)
    const showCategoryTitle =
        searchQuery ||
        (!isCategoryContentLoading && (!hasOptimizelyContent || showCategoryTitleFlag))

    return (
        <Box {...otherProps} data-testid="sf-product-list-breadcrumb">
            {searchQuery && <Seo title={searchResultsPageTitle} />}
            {/* Breadcrumb */}
            <Breadcrumb categories={category?.parentCategoryTree} />

            {/* Only show Optimizely content when it's loaded, no spinner needed */}
            {hasOptimizelyContent && (
                <OptimizelyArea
                    content={categoryContentData.items[0]}
                    area="AboveResultArea"
                    marginTop={[2, 2, 3, 3, 3]}
                />
            )}

            {/* Category Title - only show if ShowCategoryTitle is true or no Optimizely content */}
            {showCategoryTitle && (
                <>
                    <Flex align="baseline" gap={2} mt={{base: 6, lg: 8}} flexWrap="wrap">
                        <Heading
                            as="h1"
                            fontSize={{base: 24, lg: 32}}
                            textTransform="uppercase"
                            fontWeight="bold"
                        >
                            {searchQuery ? (
                                <FormattedMessage
                                    defaultMessage="SEARCH RESULT"
                                    id="search.result.heading"
                                />
                            ) : (
                                category?.name
                            )}
                        </Heading>

                        {!searchQuery && productSearchResult?.total !== undefined && (
                            <Text fontSize={12} color="grey.700" display={{base: 'none', lg: 'block'}}>
                                {formatMessage(
                                    {
                                        id: 'product_list.total_items',
                                        defaultMessage: '{count, plural, one {# item} other {# items}}'
                                    },
                                    {
                                        count: productSearchResult?.total || 0
                                    }
                                )}
                            </Text>
                        )}
                    </Flex>

                    {searchQuery && productSearchResult?.total > 0 && (
                        <Text fontSize={{base: 12, lg: 14}}>
                            {productSearchResult?.total > 0 ? (
                                <FormattedMessage
                                    defaultMessage='Showing {total} items for "{searchQuery}"'
                                    values={{
                                        total: productSearchResult?.total,
                                        // data-private: search keywords are PII for LogRocket
                                        searchQuery: <Text as="span" data-private>{searchQuery}</Text>
                                    }}
                                    id="search_page.text.items"
                                />
                            ) : (
                                <FormattedMessage
                                    defaultMessage="Sorry! No items found. But here are some recommendations for you."
                                    id="search_page.text.no_items"
                                />
                            )}
                        </Text>
                    )}
                </>
            )}
        </Box>
    )
}

PageHeader.propTypes = {
    category: PropTypes.object,
    productSearchResult: PropTypes.object,
    searchQuery: PropTypes.string,
    total: PropTypes.number,
    categoryContentData: PropTypes.object,
    isCategoryContentLoading: PropTypes.bool
}

export default PageHeader
